import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  Eraser,
  FileText,
  Loader2,
  Minus,
  Plus,
  TriangleAlert,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignaturePad, type SignaturePadHandle } from '@/components/audit/SignaturePad';
import { CylinderTankIcon, tankFromLabel } from '@/components/CylinderTankIcon';
import {
  useAuditCounts,
  useSubmitAudit,
  type SubmitAuditResult,
} from '@/hooks/useAudit';
import { compressPhoto, type CapturedPhoto } from '@/lib/imageCapture';
import { supabase } from '@/lib/supabase';

/**
 * AuditForm — the on-site audit flow for one customer:
 *
 *   1. Counts   — each cylinder bucket shows what the system thinks is on
 *                 site; the driver steppers in what they actually counted.
 *   2. Photos   — camera capture (required whenever a count differs).
 *   3. Sign-off — signature pad + printed name + today's date.
 *   4. Review   — deltas summarized; submit creates the $0 CR-ADJ invoice
 *                 when anything moved and files the Tank Rental Audit.
 */

const MAX_PHOTOS = 6;

export default function AuditForm() {
  // /audit/:customerId (the driver picker — a portal customer) or
  // /audit/qbo/:qboId (opened from the tank-rental screen — an account that
  // may exist only in QuickBooks). ?from=rentals sends the links back there.
  const { customerId, qboId } = useParams<{ customerId?: string; qboId?: string }>();
  const [search] = useSearchParams();
  const fromRentals = search.get('from') === 'rentals';
  const subjectKey = customerId ? { customerId } : qboId ? { qboId } : undefined;
  const { data, isLoading, error } = useAuditCounts(subjectKey);
  const backTo = fromRentals && (qboId || data?.customer.qbo_customer_id)
    ? `/audit/rentals/${qboId ?? data?.customer.qbo_customer_id}`
    : '/audit/count';
  const backLabel = fromRentals ? 'Back to the rental account' : 'Customers';
  const submit = useSubmitAudit();

  // counted[label] — undefined until the driver confirms/edits a bucket.
  const [counted, setCounted] = useState<Record<string, number>>({});
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Managers/admins only: file a count change without a photo, deliberately.
  const [skipPhoto, setSkipPhoto] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signatureDirty, setSignatureDirty] = useState(false);
  const [managerName, setManagerName] = useState('');
  const [managerSigDirty, setManagerSigDirty] = useState(false);
  const [notes, setNotes] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [result, setResult] = useState<SubmitAuditResult | null>(null);
  const sigRef = useRef<SignaturePadHandle>(null);
  const managerSigRef = useRef<SignaturePadHandle>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Prefill the printed name from the signed-in driver's profile.
  useEffect(() => {
    void supabase.auth.getUser().then(({ data: u }) => {
      const meta = (u.user?.user_metadata ?? {}) as { full_name?: string; name?: string };
      setSignedName((prev) => prev || meta.full_name || meta.name || '');
    });
  }, []);

  // Seed counted values from the system counts once loaded (driver adjusts
  // from there — most buckets match).
  useEffect(() => {
    if (!data) return;
    setCounted((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const seeded: Record<string, number> = {};
      for (const b of data.buckets) seeded[b.label] = Math.max(0, b.system_count);
      return seeded;
    });
  }, [data]);

  useEffect(
    () => () => photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deltas = useMemo(() => {
    if (!data) return [];
    return data.buckets
      .map((b) => ({
        label: b.label,
        system: b.system_count,
        counted: counted[b.label] ?? Math.max(0, b.system_count),
        delta: (counted[b.label] ?? Math.max(0, b.system_count)) - b.system_count,
      }))
      .filter((d) => d.delta !== 0);
  }, [data, counted]);
  const hasAdjustment = deltas.length > 0;
  // Net shortage → the backend holds the CR-ADJ invoice for manager approval.
  const isShortage = deltas.reduce((s, d) => s + d.delta, 0) < 0;

  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const canReview =
    !!data &&
    signedName.trim().length > 1 &&
    signatureDirty &&
    (!hasAdjustment || photos.length > 0 || (skipPhoto && !!data?.can_skip_photo)) &&
    // A manager signature needs a manager name to go with it.
    (!managerSigDirty || managerName.trim().length > 1);

  async function onPickPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    setPhotoError(null);
    setCompressing(true);
    try {
      const room = MAX_PHOTOS - photos.length;
      const picked = Array.from(files).slice(0, room);
      const done: CapturedPhoto[] = [];
      for (const f of picked) done.push(await compressPhoto(f));
      setPhotos((prev) => [...prev, ...done]);
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : String(err));
    } finally {
      setCompressing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function removePhoto(i: number) {
    setPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(i, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }

  async function onSubmit() {
    if (!data || !subjectKey) return;
    const signature = sigRef.current?.toDataURL();
    if (!signature) return;
    const managerSignature = managerSigRef.current?.toDataURL() ?? null;
    try {
      const res = await submit.mutateAsync({
        ...(customerId ? { customer_id: customerId } : { qbo_customer_id: qboId }),
        counts: data.buckets.map((b) => ({
          label: b.label,
          counted: counted[b.label] ?? Math.max(0, b.system_count),
        })),
        photos: photos.map((p) => ({ data_base64: p.data_base64, content_type: p.content_type })),
        signature_data_url: signature,
        signed_name: signedName.trim(),
        manager_name: managerName.trim() || undefined,
        manager_signature_data_url: managerSignature ?? undefined,
        notes: notes.trim() || undefined,
      });
      setResult(res);
      setShowReview(false);
    } catch {
      // submit.error renders in the review sheet
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading cylinder counts…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface p-5 text-sm">
        <div className="font-semibold text-brix-ink">Couldn’t load this customer.</div>
        <p className="mt-1 text-brix-muted">{error?.message ?? 'Unknown error'}</p>
        <Button asChild variant="secondary" className="mt-4">
          <Link to={backTo}>{fromRentals ? 'Back to the rental account' : 'Back to customers'}</Link>
        </Button>
      </div>
    );
  }

  // ---- success screen ----
  if (result) {
    return (
      <div className="mx-auto max-w-md pt-6 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-brix-success" />
        <h1 className="mt-3 font-display text-xl font-bold text-brix-ink">
          {result.status === 'balanced'
            ? 'Counts match — all good'
            : result.status === 'pending_approval'
              ? 'Audit sent for approval'
              : 'Audit filed'}
        </h1>
        <div className="mt-1 font-mono text-sm text-brix-muted">{result.doc_number}</div>
        <p className="mt-3 text-sm text-brix-muted">{result.note}</p>

        {result.counts.some((c) => c.delta !== 0) ? (
          <div className="mt-5 overflow-hidden rounded-xl border border-brix-border bg-brix-surface text-left shadow-card">
            {result.counts
              .filter((c) => c.delta !== 0)
              .map((c) => (
                <div
                  key={c.label}
                  className="flex items-center justify-between border-b border-brix-border px-4 py-3 text-sm last:border-b-0"
                >
                  <span className="font-semibold text-brix-ink">{c.label}</span>
                  <span className="text-brix-muted">
                    {c.system_count} → {c.counted}{' '}
                    <span
                      className={
                        c.delta > 0 ? 'font-semibold text-brix-success' : 'font-semibold text-brix-warning'
                      }
                    >
                      ({c.delta > 0 ? '+' : ''}
                      {c.delta})
                    </span>
                  </span>
                </div>
              ))}
          </div>
        ) : null}

        {result.status === 'pending_approval' ? (
          <div className="mt-4 rounded-lg border border-brix-warning/40 bg-brix-warning/10 p-3 text-left text-xs text-brix-ink">
            <TriangleAlert className="mb-1 h-4 w-4 text-brix-warning" />
            The count came in short of the records, so this audit needs a manager’s
            approval before the adjustment invoice is created. The service team has been
            emailed — nothing more to do on site.
          </div>
        ) : null}

        {result.status === 'invoice_failed' ? (
          <div className="mt-4 rounded-lg border border-brix-warning/40 bg-brix-warning/10 p-3 text-left text-xs text-brix-ink">
            <TriangleAlert className="mb-1 h-4 w-4 text-brix-warning" />
            The audit is saved with photos and signature, but the QBO adjustment invoice
            didn’t go through. The office can re-book it from the audit record.
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-2">
          {/* /invoices/:id is the CUSTOMER's page (RLS-scoped to their account);
              the office reads the same audit on the rental account's history. */}
          {result.qbo_invoice_id && !fromRentals ? (
            <Button asChild variant="secondary">
              <Link to={`/invoices/${result.qbo_invoice_id}`}>
                <FileText className="h-4 w-4" /> View adjustment invoice {result.doc_number}
              </Link>
            </Button>
          ) : null}
          <Button asChild>
            <Link to={backTo}>{fromRentals ? 'Back to the rental account' : 'Audit another customer'}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-sm text-brix-muted transition-colors hover:text-brix-ink"
      >
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </Link>
      <h1 className="mt-2 font-display text-xl font-bold tracking-display-tight text-brix-ink">
        {data.customer.name}
      </h1>
      <p className="mt-1 text-sm text-brix-muted">
        Count every tank on site, snap photos, and sign. If your count differs from the
        system, a $0 adjustment invoice is created automatically.
      </p>

      {/* ---- 1. Counts ---- */}
      <section className="mt-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wide text-brix-muted">
          1 · Tank count
        </h2>
        <div className="mt-2 overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
          {data.buckets.map((b) => {
            const value = counted[b.label] ?? Math.max(0, b.system_count);
            const delta = value - b.system_count;
            const tank = tankFromLabel(b.label);
            return (
              <div
                key={b.label}
                className="flex items-center gap-3 border-b border-brix-border px-4 py-3 last:border-b-0"
              >
                <CylinderTankIcon tone={tank.tone} size="mini" className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm font-semibold text-brix-ink">{b.label}</div>
                  <div className="text-xs text-brix-muted">
                    {/* A NEGATIVE record means more tanks came back than we ever invoiced out
                        (usually no starting count on file). The counter can't go below 0, so
                        before anybody counts, the difference reads as a "+N" that is not a
                        gain — it is the books being brought back up to what is on site. Say
                        that instead of painting it green. */}
                    System: <span className={b.system_count < 0 ? 'font-semibold text-red-700' : ''}>{b.system_count}</span>
                    {!b.tracked ? ' (not on rent)' : ''}
                    {delta !== 0 ? (
                      <span
                        className={
                          b.system_count < 0
                            ? 'ml-1 font-semibold text-brix-muted'
                            : delta > 0
                              ? 'ml-1 font-semibold text-brix-success'
                              : 'ml-1 font-semibold text-brix-warning'
                        }
                      >
                        {b.system_count < 0 ? 'records adjust ' : ''}
                        {delta > 0 ? '+' : ''}
                        {delta}
                      </span>
                    ) : null}
                  </div>
                  {b.system_count < 0 ? (
                    <div className="mt-0.5 text-[11px] text-red-700">
                      Our records show {Math.abs(b.system_count)} more picked up than delivered — count what is really on site.
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Fewer ${b.label}`}
                    onClick={() =>
                      setCounted((prev) => ({ ...prev, [b.label]: Math.max(0, value - 1) }))
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-brix-border text-brix-ink transition-colors active:bg-brix-primary-tint"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div
                    className={`w-12 text-center font-mono text-lg font-bold ${
                      delta !== 0 ? 'text-brix-primary' : 'text-brix-ink'
                    }`}
                  >
                    {value}
                  </div>
                  <button
                    type="button"
                    aria-label={`More ${b.label}`}
                    onClick={() =>
                      setCounted((prev) => ({ ...prev, [b.label]: Math.min(999, value + 1) }))
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-brix-border text-brix-ink transition-colors active:bg-brix-primary-tint"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- 2. Photos ---- */}
      <section className="mt-6">
        <h2 className="font-display text-sm font-bold uppercase tracking-wide text-brix-muted">
          2 · Photos {hasAdjustment ? <span className="text-brix-warning">(required)</span> : '(optional)'}
        </h2>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <div key={p.previewUrl} className="relative aspect-square overflow-hidden rounded-lg border border-brix-border">
              <img src={p.previewUrl} alt={`Audit photo ${i + 1}`} className="h-full w-full object-cover" />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => removePhoto(i)}
                className="absolute right-1 top-1 rounded-full bg-brix-ink/70 p-1 text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={compressing}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-brix-border bg-brix-surface text-brix-muted transition-colors hover:border-brix-primary/40 hover:text-brix-primary"
            >
              {compressing ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
              <span className="text-xs font-semibold">{compressing ? 'Adding…' : 'Take photo'}</span>
            </button>
          ) : null}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          hidden
          onChange={(e) => void onPickPhotos(e.target.files)}
        />
        {photoError ? <p className="mt-2 text-xs text-brix-warning">{photoError}</p> : null}
        {hasAdjustment && photos.length === 0 ? (
          data?.can_skip_photo ? (
            <label className="mt-2 flex items-start gap-2 text-xs text-brix-muted">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={skipPhoto}
                onChange={(e) => setSkipPhoto(e.target.checked)}
              />
              <span>
                Submit without a photo. Only managers and admins can do this, and your name is
                recorded on the audit.
              </span>
            </label>
          ) : (
            <p className="mt-2 text-xs text-brix-muted">
              Your count changed — take at least one photo of the tanks on site. Only a manager
              or admin can submit without one.
            </p>
          )
        ) : null}
      </section>

      {/* ---- 3. Sign-off ---- */}
      <section className="mt-6">
        <h2 className="font-display text-sm font-bold uppercase tracking-wide text-brix-muted">
          3 · Sign-off
        </h2>
        <div className="mt-2 rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-brix-muted">Signature</Label>
            <button
              type="button"
              onClick={() => sigRef.current?.clear()}
              className="inline-flex items-center gap-1 text-xs text-brix-muted transition-colors hover:text-brix-ink"
            >
              <Eraser className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
          <div className="mt-1 h-36 overflow-hidden rounded-lg border border-brix-border bg-white">
            <SignaturePad ref={sigRef} onDirtyChange={setSignatureDirty} />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="signed-name" className="text-xs text-brix-muted">
                Name
              </Label>
              <Input
                id="signed-name"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-brix-muted">Date</Label>
              <div className="mt-1 flex h-10 items-center rounded-lg border border-brix-border bg-brix-surface-alt px-3 text-sm text-brix-ink">
                {today}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="audit-notes" className="text-xs text-brix-muted">
              Notes (optional)
            </Label>
            <Input
              id="audit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. two spares in the back room"
              className="mt-1"
            />
          </div>
        </div>

        {/* Optional on-site manager countersignature. */}
        <div className="mt-3 rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-sm font-semibold text-brix-ink">
                Manager sign-off <span className="font-normal text-brix-muted">(optional)</span>
              </div>
              <p className="text-xs text-brix-muted">
                Have the on-site manager countersign the count if they’re available.
              </p>
            </div>
            <button
              type="button"
              onClick={() => managerSigRef.current?.clear()}
              className="inline-flex items-center gap-1 text-xs text-brix-muted transition-colors hover:text-brix-ink"
            >
              <Eraser className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
          <div className="mt-2 h-28 overflow-hidden rounded-lg border border-brix-border bg-white">
            <SignaturePad ref={managerSigRef} onDirtyChange={setManagerSigDirty} />
          </div>
          <div className="mt-3">
            <Label htmlFor="manager-name" className="text-xs text-brix-muted">
              Manager name {managerSigDirty ? <span className="text-brix-warning">(required with signature)</span> : null}
            </Label>
            <Input
              id="manager-name"
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              placeholder="On-site manager"
              autoComplete="off"
              className="mt-1"
            />
          </div>
        </div>
      </section>

      {/* ---- sticky submit bar ---- */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-brix-border bg-brix-surface/95 p-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="min-w-0 flex-1 text-xs text-brix-muted">
            {hasAdjustment ? (
              isShortage ? (
                <span>
                  <span className="font-semibold text-brix-warning">Shortage</span> — this audit
                  goes to a manager for approval before the invoice is created.
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-brix-primary">
                    {deltas.length} bucket{deltas.length === 1 ? '' : 's'} adjusted
                  </span>{' '}
                  — a $0 CR-ADJ invoice will be created.
                </span>
              )
            ) : (
              <span>Counts match the system — no invoice needed.</span>
            )}
          </div>
          <Button size="lg" disabled={!canReview} onClick={() => setShowReview(true)}>
            Review &amp; submit
          </Button>
        </div>
      </div>

      {/* ---- review sheet ---- */}
      {showReview ? (
        <div className="fixed inset-0 z-30 flex items-end justify-center bg-brix-ink/50 sm:items-center">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-brix-surface p-5 shadow-card-deep sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-brix-ink">Confirm audit</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setShowReview(false)}
                className="rounded-md p-1 text-brix-muted hover:bg-brix-primary-tint"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-1 text-sm text-brix-muted">{data.customer.name} · {today}</p>

            <div className="mt-4 overflow-hidden rounded-xl border border-brix-border">
              {data.buckets.map((b) => {
                const value = counted[b.label] ?? Math.max(0, b.system_count);
                const delta = value - b.system_count;
                return (
                  <div
                    key={b.label}
                    className="flex items-center justify-between border-b border-brix-border px-3 py-2 text-sm last:border-b-0"
                  >
                    <span className="text-brix-ink">{b.label}</span>
                    <span className="font-mono text-brix-muted">
                      {b.system_count} → {value}{' '}
                      {delta !== 0 ? (
                        <span className={delta > 0 ? 'font-bold text-brix-success' : 'font-bold text-brix-warning'}>
                          ({delta > 0 ? '+' : ''}
                          {delta})
                        </span>
                      ) : (
                        <Check className="ml-1 inline h-3.5 w-3.5 text-brix-success" />
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 text-xs text-brix-muted">
              {photos.length} photo{photos.length === 1 ? '' : 's'} · signed by{' '}
              <span className="font-semibold text-brix-ink">{signedName.trim()}</span>
              {hasAdjustment ? (
                isShortage ? (
                  <>
                    {' '}· <span className="font-semibold text-brix-warning">shortage</span> — held
                    for manager approval before the <span className="font-mono">CR-ADJ</span> invoice
                  </>
                ) : (
                  <>
                    {' '}· creates a <span className="font-mono">CR-ADJ</span> $0 adjustment invoice
                  </>
                )
              ) : null}
              {managerName.trim() ? <> · manager: {managerName.trim()}</> : null}
            </div>

            {submit.error ? (
              <div className="mt-3 rounded-lg border border-brix-warning/40 bg-brix-warning/10 p-3 text-xs text-brix-ink">
                {submit.error.message}
              </div>
            ) : null}

            <div className="mt-5 flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setShowReview(false)}>
                Back
              </Button>
              <Button className="flex-1" disabled={submit.isPending} onClick={() => void onSubmit()}>
                {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {submit.isPending ? 'Submitting…' : 'Submit audit'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
